import { useEffect, useState } from 'react';
import { CalendarDays, MapPin } from 'lucide-react';

import { fetchNextEvent, daysUntil, countdownLabel } from '../utils/upcomingEvent';

import { eventPath } from './public/eventDisplay';

// Event dates are stored as naive local (Eastern) times, matching EventsPage.
const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

/**
 * Home page banner for the next upcoming public event, with a day countdown.
 * Renders nothing when there are no upcoming events, the event day has
 * passed, or the request fails. Pulled up to overlap the bottom of the hero
 * so it starts above the fold on desktop.
 */
const UpcomingEventBanner = () => {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchNextEvent().then((next) => {
      if (!cancelled && next) setEvent(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!event) return null;
  const days = daysUntil(event.date);
  if (days < 0) return null;

  return (
    <section aria-label="Upcoming event" className="relative z-10 -mt-12 pb-8 sm:-mt-20 sm:pb-10">
      <div className="container mx-auto px-4">
        <a
          href={eventPath(event)}
          className="group mx-auto flex max-w-4xl flex-col items-center gap-6 rounded-[1.75rem] bg-gray-900 p-5 text-white shadow-[0_24px_70px_-28px_rgba(17,24,39,0.5)] ring-1 ring-gray-800 transition duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-red-600/40 sm:flex-row sm:p-6 motion-reduce:transform-none motion-reduce:transition-none"
        >
          {event.image_url && (
            <img
              src={event.image_url}
              alt={`${event.title} poster`}
              className="h-auto w-full max-w-xs rounded-2xl object-cover sm:w-56"
              loading="lazy"
            />
          )}
          <div className="text-center sm:text-left">
            <div className="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
              <p className="text-sm font-semibold uppercase tracking-wider text-red-400">Upcoming Event</p>
              <span className="rounded-full bg-amber-400 px-3 py-1 text-xs font-bold uppercase tracking-wide text-gray-900">
                {countdownLabel(days)}
              </span>
            </div>
            <h2 className="mt-2 text-2xl font-bold sm:text-3xl">{event.title}</h2>
            <p className="mt-3 flex items-center justify-center gap-2 text-gray-300 sm:justify-start">
              <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
              {formatDate(event.date)}
            </p>
            {event.location && (
              <p className="mt-1 flex items-center justify-center gap-2 text-gray-300 sm:justify-start">
                <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                {event.location}
              </p>
            )}
            <span className="mt-5 inline-block rounded-full bg-red-600 px-5 py-2 text-sm font-semibold transition group-hover:bg-red-700">
              Event details
            </span>
          </div>
        </a>
      </div>
    </section>
  );
};

export default UpcomingEventBanner;
